import { type Dispatch, type SetStateAction, type TouchEvent, useCallback, useEffect, useRef, useState } from 'react'

type ReactTouchList = TouchEvent<HTMLDivElement>['touches']
type ReactTouch = TouchEvent<HTMLDivElement>['changedTouches'][number]
type MobileImageTransform = { scale: number; x: number; y: number }
type TouchPoint = { x: number; y: number }
type TimeoutId = ReturnType<typeof setTimeout>

const DOUBLE_TAP_ZOOM_TRANSITION = 'transform 0.28s ease'
const PINCH_ZOOM_TRANSITION = 'transform 0.06s linear'

const getTouchDistance = (touches: ReactTouchList) => {
	const firstTouch = touches[0]
	const secondTouch = touches[1]
	if (!firstTouch || !secondTouch) return 0

	return Math.hypot(firstTouch.clientX - secondTouch.clientX, firstTouch.clientY - secondTouch.clientY)
}

const getTouchCenter = (touches: ReactTouchList): TouchPoint | null => {
	const firstTouch = touches[0]
	const secondTouch = touches[1]
	if (!firstTouch || !secondTouch) return null

	return {
		x: (firstTouch.clientX + secondTouch.clientX) / 2,
		y: (firstTouch.clientY + secondTouch.clientY) / 2
	}
}

const getLimitedMobileScale = (scale: number) => Math.min(Math.max(scale, 1), 3)
const getLimitedMobileTranslate = (value: number, scale: number, viewportSize: number) => {
	const maxTranslate = (viewportSize * (scale - 1)) / 2
	return Math.min(Math.max(value, -maxTranslate), maxTranslate)
}

const getMobileTranslateFromTouch = (touch: ReactTouch, element: HTMLDivElement, scale: number) => {
	const rect = element.getBoundingClientRect()
	const translateX = (rect.left + rect.width / 2 - touch.clientX) * (scale - 1)
	const translateY = (rect.top + rect.height / 2 - touch.clientY) * (scale - 1)

	return {
		x: getLimitedMobileTranslate(translateX, scale, window.innerWidth),
		y: getLimitedMobileTranslate(translateY, scale, window.innerHeight)
	}
}

const getMobileTranslateFromPinch = ({
	currentTransform,
	nextScale,
	nextTouchCenter,
	previousTouchCenter,
	imageElement
}: {
	currentTransform: MobileImageTransform
	nextScale: number
	nextTouchCenter: TouchPoint
	previousTouchCenter: TouchPoint
	imageElement: HTMLImageElement | null
}) => {
	const imageRect = imageElement?.getBoundingClientRect()
	const baseCenterX = imageRect ? imageRect.left + imageRect.width / 2 - currentTransform.x : window.innerWidth / 2
	const baseCenterY = imageRect ? imageRect.top + imageRect.height / 2 - currentTransform.y : window.innerHeight / 2
	const scaleRatio = nextScale / currentTransform.scale
	const translateX =
		nextTouchCenter.x - baseCenterX - (previousTouchCenter.x - baseCenterX - currentTransform.x) * scaleRatio
	const translateY =
		nextTouchCenter.y - baseCenterY - (previousTouchCenter.y - baseCenterY - currentTransform.y) * scaleRatio

	return {
		x: getLimitedMobileTranslate(translateX, nextScale, window.innerWidth),
		y: getLimitedMobileTranslate(translateY, nextScale, window.innerHeight)
	}
}

export const useMobileGalleryTouchZoom = ({
	isDesktop,
	isActiveVideo,
	setIsChromeHidden,
	setIsReviewExpanded
}: {
	isDesktop: boolean
	isActiveVideo: boolean
	setIsChromeHidden: Dispatch<SetStateAction<boolean>>
	setIsReviewExpanded: Dispatch<SetStateAction<boolean>>
}) => {
	const [mobileImageScale, setMobileImageScale] = useState(1)
	const mobileImageElementRef = useRef<HTMLImageElement | null>(null)
	const mobileImageTransformRef = useRef<MobileImageTransform>({ scale: 1, x: 0, y: 0 })
	const mobileImageStateScaleRef = useRef(1)
	const mobileTransformFrameRef = useRef<number | null>(null)
	const mobileTransitionTimeoutRef = useRef<TimeoutId | null>(null)
	const suppressNextMediaClickTimeoutRef = useRef<TimeoutId | null>(null)
	const lastMobileTapTimeRef = useRef(0)
	const mobilePinchDistanceRef = useRef<number | null>(null)
	const mobilePinchScaleRef = useRef(1)
	const mobilePinchPointRef = useRef<TouchPoint | null>(null)
	const mobilePanPointRef = useRef<TouchPoint | null>(null)
	const hasMobileTouchMovedRef = useRef(false)
	const suppressNextMediaClickRef = useRef(false)

	const isMobileImageZoomed = !isDesktop && mobileImageScale > 1 && !isActiveVideo

	const applyMobileImageTransform = useCallback(() => {
		const imageElement = mobileImageElementRef.current
		if (!imageElement) return

		const { scale, x, y } = mobileImageTransformRef.current
		imageElement.style.transform = scale > 1 ? `translate3d(${x}px, ${y}px, 0) scale(${scale})` : ''
	}, [])

	const clearMobileImageTransition = useCallback(() => {
		if (mobileTransitionTimeoutRef.current !== null) {
			clearTimeout(mobileTransitionTimeoutRef.current)
			mobileTransitionTimeoutRef.current = null
		}

		const imageElement = mobileImageElementRef.current
		if (imageElement) imageElement.style.transition = ''
	}, [])

	const suppressNextMediaClick = useCallback(() => {
		suppressNextMediaClickRef.current = true

		if (suppressNextMediaClickTimeoutRef.current !== null) {
			clearTimeout(suppressNextMediaClickTimeoutRef.current)
		}

		suppressNextMediaClickTimeoutRef.current = setTimeout(() => {
			suppressNextMediaClickRef.current = false
			suppressNextMediaClickTimeoutRef.current = null
		}, 350)
	}, [])

	const animateNextMobileImageTransform = useCallback(() => {
		clearMobileImageTransition()

		const imageElement = mobileImageElementRef.current
		if (!imageElement) return

		imageElement.style.transition = DOUBLE_TAP_ZOOM_TRANSITION
		mobileTransitionTimeoutRef.current = setTimeout(() => {
			imageElement.style.transition = ''
			mobileTransitionTimeoutRef.current = null
		}, 300)
	}, [clearMobileImageTransition])

	const setMobilePinchTransition = useCallback(() => {
		if (mobileTransitionTimeoutRef.current !== null) {
			clearTimeout(mobileTransitionTimeoutRef.current)
			mobileTransitionTimeoutRef.current = null
		}

		const imageElement = mobileImageElementRef.current
		if (imageElement) imageElement.style.transition = PINCH_ZOOM_TRANSITION
	}, [])

	const scheduleMobileImageTransform = useCallback(() => {
		if (mobileTransformFrameRef.current !== null) return

		mobileTransformFrameRef.current = requestAnimationFrame(() => {
			mobileTransformFrameRef.current = null
			applyMobileImageTransform()
		})
	}, [applyMobileImageTransform])

	const setMobileImageTransform = useCallback(
		(nextTransform: MobileImageTransform, syncState = false) => {
			const wasZoomed = mobileImageStateScaleRef.current > 1
			const isZoomed = nextTransform.scale > 1

			mobileImageTransformRef.current = nextTransform
			scheduleMobileImageTransform()

			if (syncState || wasZoomed !== isZoomed) {
				mobileImageStateScaleRef.current = nextTransform.scale
				setMobileImageScale(nextTransform.scale)
			}
		},
		[scheduleMobileImageTransform]
	)

	const setMobileImageElement = useCallback(
		(node: HTMLImageElement | null) => {
			mobileImageElementRef.current = node
			if (node) applyMobileImageTransform()
		},
		[applyMobileImageTransform]
	)

	const resetMobileZoom = useCallback(() => {
		clearMobileImageTransition()
		setMobileImageTransform({ scale: 1, x: 0, y: 0 }, true)
		lastMobileTapTimeRef.current = 0
		mobilePinchDistanceRef.current = null
		mobilePinchPointRef.current = null
		mobilePanPointRef.current = null
		hasMobileTouchMovedRef.current = false
		if (suppressNextMediaClickTimeoutRef.current !== null) {
			clearTimeout(suppressNextMediaClickTimeoutRef.current)
			suppressNextMediaClickTimeoutRef.current = null
		}
		suppressNextMediaClickRef.current = false
	}, [clearMobileImageTransition, setMobileImageTransform])

	useEffect(() => {
		if (isDesktop || isActiveVideo) resetMobileZoom()
	}, [isActiveVideo, isDesktop, resetMobileZoom])

	useEffect(() => {
		return () => {
			if (mobileTransformFrameRef.current !== null) {
				cancelAnimationFrame(mobileTransformFrameRef.current)
			}
			if (mobileTransitionTimeoutRef.current !== null) clearTimeout(mobileTransitionTimeoutRef.current)
			if (suppressNextMediaClickTimeoutRef.current !== null) clearTimeout(suppressNextMediaClickTimeoutRef.current)
		}
	}, [])

	const consumeSuppressedMediaClick = useCallback(() => {
		if (!suppressNextMediaClickRef.current) return false

		if (suppressNextMediaClickTimeoutRef.current !== null) {
			clearTimeout(suppressNextMediaClickTimeoutRef.current)
			suppressNextMediaClickTimeoutRef.current = null
		}
		suppressNextMediaClickRef.current = false
		return true
	}, [])

	const handleMediaTouchStart = useCallback(
		(event: TouchEvent<HTMLDivElement>) => {
			if (isDesktop || isActiveVideo) return

			const currentScale = mobileImageTransformRef.current.scale
			hasMobileTouchMovedRef.current = false
			clearMobileImageTransition()

			if (event.touches.length === 2) {
				event.stopPropagation()
				setMobilePinchTransition()
				mobilePinchDistanceRef.current = getTouchDistance(event.touches)
				mobilePinchScaleRef.current = currentScale
				mobilePinchPointRef.current = getTouchCenter(event.touches)
				mobilePanPointRef.current = null
				return
			}

			if (event.touches.length === 1 && currentScale > 1) {
				event.stopPropagation()
				const touch = event.touches[0]
				mobilePanPointRef.current = { x: touch.clientX, y: touch.clientY }
			}
		},
		[clearMobileImageTransition, isActiveVideo, isDesktop, setMobilePinchTransition]
	)

	const handleMediaTouchMove = useCallback(
		(event: TouchEvent<HTMLDivElement>) => {
			if (isDesktop || isActiveVideo) return

			if (event.touches.length === 2 && mobilePinchDistanceRef.current) {
				event.preventDefault()
				event.stopPropagation()

				const nextDistance = getTouchDistance(event.touches)
				const nextScale = getLimitedMobileScale(
					mobilePinchScaleRef.current * (nextDistance / mobilePinchDistanceRef.current)
				)
				const nextTouchCenter = getTouchCenter(event.touches)
				const currentTransform = mobileImageTransformRef.current
				const previousTouchCenter = mobilePinchPointRef.current ?? nextTouchCenter
				if (!nextTouchCenter || !previousTouchCenter) return
				const nextTranslate = getMobileTranslateFromPinch({
					currentTransform,
					imageElement: mobileImageElementRef.current,
					nextScale,
					nextTouchCenter,
					previousTouchCenter
				})

				mobilePinchPointRef.current = nextTouchCenter
				setMobileImageTransform({
					scale: nextScale,
					x: nextTranslate.x,
					y: nextTranslate.y
				})
				setIsReviewExpanded(false)
				setIsChromeHidden(true)
				hasMobileTouchMovedRef.current = true
				return
			}

			const currentTransform = mobileImageTransformRef.current

			if (event.touches.length === 1 && currentTransform.scale > 1 && mobilePanPointRef.current) {
				event.preventDefault()
				event.stopPropagation()

				const touch = event.touches[0]
				const deltaX = touch.clientX - mobilePanPointRef.current.x
				const deltaY = touch.clientY - mobilePanPointRef.current.y

				mobilePanPointRef.current = { x: touch.clientX, y: touch.clientY }
				hasMobileTouchMovedRef.current = true

				setMobileImageTransform({
					scale: currentTransform.scale,
					x: getLimitedMobileTranslate(currentTransform.x + deltaX, currentTransform.scale, window.innerWidth),
					y: getLimitedMobileTranslate(currentTransform.y + deltaY, currentTransform.scale, window.innerHeight)
				})
			}
		},
		[isActiveVideo, isDesktop, setIsChromeHidden, setIsReviewExpanded, setMobileImageTransform]
	)

	const handleMediaTouchEnd = useCallback(
		(event: TouchEvent<HTMLDivElement>) => {
			if (isDesktop || isActiveVideo) return

			const currentScale = mobileImageTransformRef.current.scale

			if (currentScale > 1) {
				event.stopPropagation()
			}

			if (event.touches.length < 2) {
				mobilePinchDistanceRef.current = null
				mobilePinchPointRef.current = null
				mobilePinchScaleRef.current = currentScale
				clearMobileImageTransition()
			}

			if (event.touches.length === 0) {
				mobilePanPointRef.current = null
			}

			if (hasMobileTouchMovedRef.current) {
				lastMobileTapTimeRef.current = 0
				suppressNextMediaClick()
				return
			}

			if (event.changedTouches.length !== 1) return

			const now = Date.now()
			if (now - lastMobileTapTimeRef.current < 300) {
				const shouldResetScale = currentScale > 1
				const nextScale = shouldResetScale ? 1 : 2.5
				const touch = event.changedTouches[0]

				event.preventDefault()
				event.stopPropagation()
				suppressNextMediaClick()
				setIsReviewExpanded(false)
				setIsChromeHidden(!shouldResetScale)
				animateNextMobileImageTransform()
				setMobileImageTransform(
					shouldResetScale || !touch
						? { scale: 1, x: 0, y: 0 }
						: { scale: nextScale, ...getMobileTranslateFromTouch(touch, event.currentTarget, nextScale) },
					true
				)
				lastMobileTapTimeRef.current = 0
				return
			}

			lastMobileTapTimeRef.current = now
		},
		[
			animateNextMobileImageTransform,
			clearMobileImageTransition,
			isActiveVideo,
			isDesktop,
			setIsChromeHidden,
			setIsReviewExpanded,
			setMobileImageTransform,
			suppressNextMediaClick
		]
	)

	return {
		consumeSuppressedMediaClick,
		handleMediaTouchEnd,
		handleMediaTouchMove,
		handleMediaTouchStart,
		isMobileImageZoomed,
		mobileImageRef: setMobileImageElement,
		resetMobileZoom
	}
}
